import React, { useMemo, useState, useCallback } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTip, ReferenceLine } from 'recharts';
import { useMarketData, useTrading, useEngine } from '../context/MarketContext';
import { INSTRUMENTS } from '../constants/instruments';
import { fetchTradeSeries } from '../api/series';
import { Panel } from '../components/ui';
import type { Trade } from '../types';

function fmtTZS(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function pct(n: number | null | undefined, decimals = 1): string {
  if (n == null || !isFinite(n)) return '—';
  return `${n.toFixed(decimals)}%`;
}

// Normalise any symbol (secId or label) to its display label
function labelForSym(sym: string): string {
  return (
    INSTRUMENTS.find(
      i => i.secId.toUpperCase() === sym.toUpperCase() || i.label.toUpperCase() === sym.toUpperCase()
    )?.label ?? sym
  );
}

// Check if a trade/order symbol matches a filter (secId or label match)
function symMatches(tradeSym: string | undefined, filter: string): boolean {
  if (!tradeSym) return false;
  const u = tradeSym.toUpperCase();
  const f = filter.toUpperCase();
  const inst = INSTRUMENTS.find(i => i.secId.toUpperCase() === f || i.label.toUpperCase() === f);
  return u === f || (inst != null && (u === inst.secId.toUpperCase() || u === inst.label.toUpperCase()));
}

function KpiChip({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: '#0a0d10', borderRadius: 8, padding: '10px 14px', border: '1px solid #2a3540', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em' }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color ?? '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

type PostTradeMode = 'live' | 'historical';

function fmtDate(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function PostTradePage() {
  const { recentTrades } = useMarketData();
  const { orders, positions, portfolio } = useTrading();
  const { settlementReport, performanceSnapshot, inventoryBySymbol } = useEngine();

  // ── Mode toggle (live session vs. historical date range) ─────────────────────
  const [mode, setMode] = useState<PostTradeMode>('live');
  const [histStart, setHistStart] = useState<string>('');
  const [histEnd, setHistEnd]   = useState<string>('');
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState<string | null>(null);
  const [historicalTrades, setHistoricalTrades] = useState<Trade[] | null>(null);

  const fetchHistoricalTrades = useCallback(async () => {
    if (!histStart || !histEnd) { setHistError('Select a date range.'); return; }
    setHistLoading(true); setHistError(null);
    try {
      const results = await Promise.all(
        INSTRUMENTS.map(inst =>
          fetchTradeSeries(
            inst.secId,
            `${histStart}T00:00:00.000Z`,
            `${histEnd}T23:59:59.999Z`,
            '1 minute',
          ).then(rows =>
            rows
              .filter(r => r.price != null)
              .map(r => ({
                time: r.ts,
                price: r.price as number,
                size: r.volume,
                side: 'buy' as const,   // side not available in time-bucketed series
                symbol: inst.secId,
                realized_pnl: undefined,
                is_maker: undefined,
              } satisfies Trade))
          )
        )
      );
      setHistoricalTrades(
        results.flat().sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      );
    } catch (e) {
      setHistError(e instanceof Error ? e.message : 'Fetch failed');
    } finally {
      setHistLoading(false);
    }
  }, [histStart, histEnd]);

  // Single source of truth for trade data — context in live mode, fetched in historical
  const sourceTrades: Trade[] = (mode === 'historical' && historicalTrades != null)
    ? historicalTrades
    : recentTrades;

  // ── Symbol filter state (null = All) ────────────────────────────────────────
  const [filterSym, setFilterSym] = useState<string | null>(null);

  // Unique symbols active (union of positions + sourceTrades)
  const activeSymbols = useMemo(() => {
    const set = new Set<string>();
    for (const p of positions) set.add(labelForSym(p.symbol));
    for (const t of sourceTrades) { if (t.symbol) set.add(labelForSym(t.symbol)); }
    return Array.from(set).sort();
  }, [positions, sourceTrades]);

  // Resolved label for the current filter
  const filterLabel = filterSym ? labelForSym(filterSym) : null;

  // ── Zone 1 KPI computations (derived from sourceTrades) ──────────────────────
  const kpis = useMemo(() => {
    const trades = sourceTrades.filter(t => t.realized_pnl != null);
    const winCount = trades.filter(t => (t.realized_pnl ?? 0) > 0).length;
    const winRate = trades.length > 0 ? (winCount / trades.length) * 100 : null;
    const makerCount = trades.filter(t => t.is_maker).length;
    const makerPct = trades.length > 0 ? (makerCount / trades.length) * 100 : null;
    const returnPct = settlementReport && Number.isFinite(settlementReport.tradeable_cash) && settlementReport.tradeable_cash > 100
      ? (portfolio.totalPnL / settlementReport.tradeable_cash) * 100
      : null;
    return { winRate, makerPct, returnPct };
  }, [sourceTrades, portfolio.totalPnL, settlementReport]);

  // ── Zone 2 left: cumulative P&L curve (filtered) ─────────────────────────────
  const cumulativePnL = useMemo(() => {
    const tradesForCurve = filterSym
      ? sourceTrades.filter(t => symMatches(t.symbol, filterSym))
      : sourceTrades;
    const sorted = [...tradesForCurve]
      .filter(t => t.realized_pnl != null)
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    let running = 0;
    return sorted.map(t => {
      running += t.realized_pnl ?? 0;
      const d = new Date(t.time);
      const label = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      return { time: label, pnl: running };
    });
  }, [sourceTrades, filterSym]);

  const finalPnL = cumulativePnL.length > 0 ? cumulativePnL[cumulativePnL.length - 1].pnl : 0;
  const lineColor = finalPnL >= 0 ? '#36c98e' : '#ff5c5c';

  // ── Zone 2 right: P&L by symbol bars (filtered) ─────────────────────────────
  const symbolPnL = useMemo(() => {
    const src = filterSym
      ? positions.filter(p => symMatches(p.symbol, filterSym))
      : positions;
    return [...src]
      .filter(p => (p.totalPnL ?? 0) !== 0)
      .sort((a, b) => Math.abs(b.totalPnL ?? 0) - Math.abs(a.totalPnL ?? 0))
      .slice(0, 8)
      .map(p => ({ symbol: labelForSym(p.symbol), value: p.totalPnL ?? 0 }));
  }, [positions, filterSym]);
  const maxSymPnL = symbolPnL.length > 0 ? Math.max(...symbolPnL.map(s => Math.abs(s.value))) : 1;

  // ── Zone 3 col 1: maker/taker (filtered) ────────────────────────────────────
  const makerTaker = useMemo(() => {
    const trades = filterSym
      ? sourceTrades.filter(t => symMatches(t.symbol, filterSym))
      : sourceTrades;
    const makerCount = trades.filter(t => t.is_maker).length;
    const takerCount = trades.length - makerCount;
    const makerBarPct = trades.length > 0 ? (makerCount / trades.length) * 100 : 50;
    return { makerCount, takerCount, makerBarPct, total: trades.length };
  }, [sourceTrades, filterSym]);

  // ── Zone 3 col 2: trade P&L distribution (filtered) ─────────────────────────
  const histogram = useMemo(() => {
    const tradesForHist = filterSym
      ? sourceTrades.filter(t => symMatches(t.symbol, filterSym))
      : sourceTrades;
    const vals = tradesForHist.map(t => t.realized_pnl ?? 0).filter(v => v !== 0);
    if (vals.length < 5) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (min === max) return null;
    const binCount = 7;
    const step = (max - min) / binCount;
    const bins = Array.from({ length: binCount }, (_, i) => ({
      low: min + i * step,
      high: min + (i + 1) * step,
      count: 0,
    }));
    for (const v of vals) {
      const idx = Math.min(Math.floor((v - min) / step), binCount - 1);
      bins[idx].count++;
    }
    const maxCount = Math.max(...bins.map(b => b.count), 1);
    return bins.map(b => ({
      ...b,
      heightPct: (b.count / maxCount) * 100,
      isPositive: (b.low + b.high) / 2 >= 0,
    }));
  }, [sourceTrades, filterSym]);

  // ── Zone 3 col 3: fill stats (filtered) ─────────────────────────────────────
  const fillStats = useMemo(() => {
    const ordersForStats = filterSym
      ? orders.filter(o => symMatches(o.symbol, filterSym))
      : orders;
    const tradesForStats = filterSym
      ? sourceTrades.filter(t => symMatches(t.symbol, filterSym))
      : sourceTrades;

    const symMap = new Map<string, { orders: number; fills: number; pnlSum: number; pnlCount: number }>();
    for (const o of ordersForStats) {
      const s = symMap.get(o.symbol) ?? { orders: 0, fills: 0, pnlSum: 0, pnlCount: 0 };
      s.orders++;
      if (['filled', 'partially_filled'].includes(o.status)) s.fills++;
      symMap.set(o.symbol, s);
    }
    for (const t of tradesForStats) {
      if (t.symbol) {
        // match trade symbol to an order symbol key
        const key = Array.from(symMap.keys()).find(k => symMatches(t.symbol, k)) ?? t.symbol;
        if (symMap.has(key)) {
          const s = symMap.get(key)!;
          s.pnlSum += t.realized_pnl ?? 0;
          s.pnlCount++;
        }
      }
    }
    return Array.from(symMap.entries()).map(([sym, s]) => ({
      label: labelForSym(sym),
      orders: s.orders,
      fills: s.fills,
      fillRate: s.orders > 0 ? (s.fills / s.orders) * 100 : 0,
      avgPnl: s.pnlCount > 0 ? s.pnlSum / s.pnlCount : null,
    }));
  }, [orders, sourceTrades, filterSym]);

  // ── Zone 4 left: P&L attribution (filtered) ──────────────────────────────────
  const positionsForAttrib = useMemo(() => {
    const src = filterSym
      ? positions.filter(p => symMatches(p.symbol, filterSym))
      : [...positions].sort((a, b) => Math.abs(b.totalPnL ?? 0) - Math.abs(a.totalPnL ?? 0));
    return src;
  }, [positions, filterSym]);
  const totalAbsPnL = positionsForAttrib.reduce((s, p) => s + Math.abs(p.totalPnL ?? 0), 0);

  // ── Session volume (from sourceTrades) ───────────────────────────────────────
  const totalVolume = useMemo(
    () => sourceTrades.reduce((s, t) => s + t.size * t.price, 0),
    [sourceTrades]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Zone 1: Session KPIs (always session-wide) ── */}
      <Panel title="Session performance" badge="Session-wide · all counters">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <KpiChip
            label="Return %"
            value={kpis.returnPct != null ? `${kpis.returnPct >= 0 ? '+' : ''}${kpis.returnPct.toFixed(2)}%` : '—'}
            color={kpis.returnPct == null ? '#7e8a96' : kpis.returnPct >= 0 ? '#36c98e' : '#ff5c5c'}
          />
          <KpiChip
            label="Sharpe Ratio"
            value={mode === 'historical' ? '— live' : performanceSnapshot?.sharpe_ratio != null ? performanceSnapshot.sharpe_ratio.toFixed(2) : '—'}
            color={
              mode === 'historical' ? '#2a3540'
              : performanceSnapshot?.sharpe_ratio == null ? '#7e8a96'
              : performanceSnapshot.sharpe_ratio >= 1 ? '#36c98e'
              : performanceSnapshot.sharpe_ratio >= 0 ? '#ff8c3a'
              : '#ff5c5c'
            }
          />
          <KpiChip
            label="Win Rate"
            value={kpis.winRate != null ? pct(kpis.winRate) : '—'}
            color={kpis.winRate == null ? '#7e8a96' : kpis.winRate >= 50 ? '#36c98e' : '#ff5c5c'}
          />
          <KpiChip
            label="Exec Rate"
            value={mode === 'historical' ? '— live' : performanceSnapshot ? pct(performanceSnapshot.execution_rate * 100) : '—'}
            color={
              mode === 'historical' ? '#2a3540'
              : performanceSnapshot == null ? '#7e8a96'
              : performanceSnapshot.execution_rate * 100 >= 2 ? '#36c98e'
              : '#ff8c3a'
            }
          />
          <KpiChip
            label="Maker %"
            value={kpis.makerPct != null ? pct(kpis.makerPct) : '—'}
            color="#7e8a96"
          />
          <KpiChip
            label="Avg Spread Capture"
            value={mode === 'historical' ? '— live' : performanceSnapshot ? `${performanceSnapshot.avg_spread_capture_bps.toFixed(1)} bps` : '—'}
            color={
              mode === 'historical' ? '#2a3540'
              : performanceSnapshot == null ? '#7e8a96'
              : performanceSnapshot.avg_spread_capture_bps > 0 ? '#36c98e'
              : '#ff5c5c'
            }
          />
        </div>
      </Panel>

      {/* ── Mode Toggle & Date Range ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* Live / Date Range buttons */}
        {(['live', 'historical'] as PostTradeMode[]).map(m => (
          <button
            key={m}
            onClick={() => { setMode(m); if (m === 'live') setHistoricalTrades(null); }}
            style={{
              padding: '5px 14px', borderRadius: 5, cursor: 'pointer',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
              background: mode === m ? '#4da3ff' : '#10151b',
              color: mode === m ? '#fff' : '#7a8693',
              border: `1px solid ${mode === m ? '#4da3ff' : '#2a3540'}`,
            }}
          >
            {m === 'live' ? 'LIVE SESSION' : 'DATE RANGE'}
          </button>
        ))}

        {mode === 'historical' && (
          <>
            <input
              type="date"
              value={histStart}
              onChange={e => setHistStart(e.target.value)}
              style={{ background: '#0a0d10', border: '1px solid #2a3540', color: '#d7dde4', borderRadius: 4, padding: '4px 8px', fontSize: 11 }}
            />
            <span style={{ color: '#46525e', fontSize: 11 }}>→</span>
            <input
              type="date"
              value={histEnd}
              onChange={e => setHistEnd(e.target.value)}
              style={{ background: '#0a0d10', border: '1px solid #2a3540', color: '#d7dde4', borderRadius: 4, padding: '4px 8px', fontSize: 11 }}
            />
            {/* Presets */}
            {([['Today', 0], ['7D', 7], ['30D', 30]] as [string, number][]).map(([label, offset]) => (
              <button
                key={label}
                onClick={() => { setHistEnd(fmtDate(0)); setHistStart(fmtDate(offset)); }}
                style={{ padding: '3px 8px', background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 4, color: '#7a8693', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
              >
                {label}
              </button>
            ))}
            <button
              onClick={fetchHistoricalTrades}
              disabled={histLoading}
              style={{ padding: '4px 12px', background: histLoading ? '#10151b' : '#4da3ff', border: 'none', borderRadius: 4, color: histLoading ? '#7a8693' : '#fff', fontSize: 11, fontWeight: 700, cursor: histLoading ? 'default' : 'pointer' }}
            >
              {histLoading ? 'Loading…' : 'Fetch'}
            </button>
            {histError && <span style={{ fontSize: 10, color: '#ff5c5c' }}>{histError}</span>}
          </>
        )}
      </div>

      {/* Historical mode caveat */}
      {mode === 'historical' && (
        <div style={{ padding: '6px 12px', background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 6, fontSize: 10, color: '#7a8693' }}>
          Orders, positions, settlement, and engine performance metrics always show live session data.
          Trade buy/sell side is unavailable in time-bucketed series (shown as placeholder).
          {/* TODO: Full historical orders require GET /api/orders?symbol&start&end endpoint */}
        </div>
      )}

      {/* ── Symbol Filter Bar ── */}
      {activeSymbols.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', flexShrink: 0 }}>VIEW:</span>
          {/* ALL pill */}
          <button
            onClick={() => setFilterSym(null)}
            style={{
              background: filterSym === null ? 'rgba(77,163,255,0.12)' : '#0a0d10',
              border: `1px solid ${filterSym === null ? '#4da3ff' : '#2a3540'}`,
              borderRadius: 4, padding: '3px 10px', cursor: 'pointer',
              fontSize: 11, fontWeight: 700,
              color: filterSym === null ? '#93c5fd' : '#7a8693',
            }}
          >
            ALL
          </button>
          {activeSymbols.map(sym => {
            const active = filterLabel === sym;
            return (
              <button
                key={sym}
                onClick={() => setFilterSym(sym)}
                style={{
                  background: active ? 'rgba(77,163,255,0.12)' : '#0a0d10',
                  border: `1px solid ${active ? '#4da3ff' : '#2a3540'}`,
                  borderRadius: 4, padding: '3px 10px', cursor: 'pointer',
                  fontSize: 11, fontWeight: 700,
                  color: active ? '#93c5fd' : '#7a8693',
                }}
              >
                {sym}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Zone 2: P&L Panel ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>

        {/* Cumulative realized P&L curve */}
        <Panel title="Cumulative realized P&L" badge={filterLabel}>
          {cumulativePnL.length === 0 ? (
            <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2a3540', fontSize: 12, fontStyle: 'italic' }}>
              {filterLabel ? `No trades for ${filterLabel} yet` : 'No trades this session yet'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={cumulativePnL} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="10%" stopColor={lineColor} stopOpacity={0.25} />
                    <stop offset="90%" stopColor={lineColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#46525e' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: '#46525e' }} tickLine={false} axisLine={false} tickFormatter={v => fmtTZS(v)} width={48} />
                <ReferenceLine y={0} stroke="#2a3540" strokeDasharray="4 2" />
                <RechartsTip
                  contentStyle={{ background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 6, fontSize: 11 }}
                  formatter={(v) => [`TZS ${(typeof v === 'number' && Number.isFinite(v) ? v : 0).toLocaleString()}`, 'Cumulative P&L']}
                />
                <Area type="monotone" dataKey="pnl" stroke={lineColor} strokeWidth={2} fill="url(#pnlGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* P&L by symbol — diverging bars */}
        <Panel title="P&L by symbol" badge={filterLabel}>
          {symbolPnL.length === 0 ? (
            <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2a3540', fontSize: 12, fontStyle: 'italic' }}>
              No position P&L yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {symbolPnL.map(({ symbol, value }) => {
                const barPct = (Math.abs(value) / maxSymPnL) * 100;
                const color = value >= 0 ? '#36c98e' : '#ff5c5c';
                return (
                  <div key={symbol} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#7e8a96', width: 60, flexShrink: 0 }}>{symbol}</span>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', height: 14 }}>
                      <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, backgroundColor: '#2a3540' }} />
                      <div style={{
                        position: 'absolute', height: '100%',
                        width: `${barPct / 2}%`, maxWidth: '50%',
                        backgroundColor: color, borderRadius: 2,
                        left: value >= 0 ? '50%' : undefined,
                        right: value < 0 ? '50%' : undefined,
                      }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color, minWidth: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {value >= 0 ? '+' : ''}{fmtTZS(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* ── Zone 3: Execution Quality ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>

        {/* Col 1: Maker / Taker */}
        <Panel title="Maker / taker" badge={filterLabel}>
          {makerTaker.total === 0 ? (
            <div style={{ color: '#2a3540', fontSize: 12, fontStyle: 'italic' }}>No trades yet</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>MAKER</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#36c98e', fontVariantNumeric: 'tabular-nums' }}>{makerTaker.makerBarPct.toFixed(0)}%</div>
                  <div style={{ fontSize: 10, color: '#46525e' }}>{makerTaker.makerCount} trades</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>TAKER</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#ff8c3a', fontVariantNumeric: 'tabular-nums' }}>{(100 - makerTaker.makerBarPct).toFixed(0)}%</div>
                  <div style={{ fontSize: 10, color: '#46525e' }}>{makerTaker.takerCount} trades</div>
                </div>
              </div>
              <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#0a0d10', display: 'flex' }}>
                <div style={{ width: `${makerTaker.makerBarPct}%`, backgroundColor: '#36c98e', transition: 'width 0.4s' }} />
                <div style={{ flex: 1, backgroundColor: '#ff8c3a' }} />
              </div>
            </>
          )}
        </Panel>

        {/* Col 2: Trade P&L distribution */}
        <Panel title="Trade P&L distribution" badge={filterLabel}>
          {!histogram ? (
            <div style={{ color: '#2a3540', fontSize: 12, fontStyle: 'italic' }}>{'< 5 trades with P&L data'}</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
              {histogram.map((b, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <div style={{ fontSize: 8, color: b.count > 0 ? (b.isPositive ? '#36c98e' : '#ff5c5c') : '#2a3540' }}>{b.count}</div>
                  <div style={{
                    width: '100%', height: `${b.heightPct}%`,
                    minHeight: b.count > 0 ? 4 : 0,
                    backgroundColor: b.isPositive ? '#36c98e' : '#ff5c5c',
                    opacity: b.count > 0 ? 1 : 0.15,
                    borderRadius: '2px 2px 0 0',
                  }} />
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Col 3: Fill stats */}
        <Panel title={filterLabel ? 'Fill stats' : 'Fill stats by symbol'} badge={filterLabel}>
          {fillStats.length === 0 ? (
            <div style={{ color: '#2a3540', fontSize: 12, fontStyle: 'italic' }}>No orders yet</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '52px 40px 40px 52px 60px', gap: 6, padding: '3px 0', borderBottom: '1px solid #2a3540', fontSize: 9, fontWeight: 700, color: '#46525e', letterSpacing: '0.05em' }}>
                <div>SYM</div>
                <div style={{ textAlign: 'right' }}>ORD</div>
                <div style={{ textAlign: 'right' }}>FILLS</div>
                <div style={{ textAlign: 'right' }}>RATE</div>
                <div style={{ textAlign: 'right' }}>AVG P&amp;L</div>
              </div>
              {fillStats.map(s => (
                <div key={s.label} style={{ display: 'grid', gridTemplateColumns: '52px 40px 40px 52px 60px', gap: 6, padding: '6px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#d7dde4' }}>{s.label}</div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#7e8a96' }}>{s.orders}</div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#7e8a96' }}>{s.fills}</div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: s.fillRate >= 50 ? '#36c98e' : '#ff8c3a' }}>{s.fillRate.toFixed(0)}%</div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: s.avgPnl == null ? '#46525e' : s.avgPnl >= 0 ? '#36c98e' : '#ff5c5c', fontVariantNumeric: 'tabular-nums' }}>
                    {s.avgPnl == null ? '—' : `${s.avgPnl >= 0 ? '+' : ''}${fmtTZS(s.avgPnl)}`}
                  </div>
                </div>
              ))}
            </>
          )}
        </Panel>
      </div>

      {/* ── Zone 4: Position & Capital Summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>

        {/* Left: P&L Attribution */}
        <Panel title={filterLabel ? 'P&L attribution' : 'P&L attribution by symbol'} badge={filterLabel}>
          {positionsForAttrib.length === 0 ? (
            <div style={{ color: '#2a3540', fontSize: 12, fontStyle: 'italic', padding: '16px 0' }}>
              {filterLabel ? `No position for ${filterLabel}` : 'No positions yet'}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 52px', gap: 8, padding: '4px 0', borderBottom: '1px solid #2a3540', fontSize: 9, fontWeight: 700, color: '#46525e', letterSpacing: '0.06em' }}>
                <div>SYMBOL</div>
                <div style={{ textAlign: 'right' }}>UNREALIZED</div>
                <div style={{ textAlign: 'right' }}>REALIZED</div>
                <div style={{ textAlign: 'right' }}>TOTAL P&amp;L</div>
                <div style={{ textAlign: 'right' }}>EXPOSURE</div>
                <div style={{ textAlign: 'right' }}>SHARE</div>
              </div>
              {positionsForAttrib.map((p, i) => {
                const total = p.totalPnL ?? 0;
                const sharePct = totalAbsPnL > 0 ? (Math.abs(total) / totalAbsPnL) * 100 : 0;
                const col = total >= 0 ? '#36c98e' : '#ff5c5c';
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 52px', gap: 8, padding: '7px 0', borderBottom: '1px solid #0a0d10', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#d7dde4' }}>{labelForSym(p.symbol)}</div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: p.unrealizedPnL >= 0 ? '#36c98e' : '#ff5c5c' }}>
                      {p.unrealizedPnL.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: p.realizedPnL >= 0 ? '#36c98e' : '#ff5c5c' }}>
                      {p.realizedPnL.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: col }}>
                      {total >= 0 ? '+' : ''}{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: '#7a8693' }}>
                      {(p.exposure ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                      <div style={{ height: 4, width: 24, backgroundColor: '#0a0d10', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${sharePct}%`, backgroundColor: col, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 9, color: '#46525e' }}>{sharePct.toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </Panel>

        {/* Right: Capital & Inventory Health (always global context) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          <Panel title="Capital status">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ background: '#0a0d10', borderRadius: 6, border: '1px solid #2a3540', padding: '8px 10px' }}>
                <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>UTILIZATION</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: performanceSnapshot && performanceSnapshot.capital_utilization > 0.85 ? '#ff5c5c' : '#d7dde4' }}>
                  {performanceSnapshot ? pct(performanceSnapshot.capital_utilization * 100) : '—'}
                </div>
              </div>
              <div style={{ background: '#0a0d10', borderRadius: 6, border: '1px solid #2a3540', padding: '8px 10px' }}>
                <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>MAX DRAWDOWN</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#ff5c5c' }}>
                  {performanceSnapshot ? pct(performanceSnapshot.max_drawdown * 100) : '—'}
                </div>
              </div>
              <div style={{ background: '#0a0d10', borderRadius: 6, border: '1px solid #2a3540', padding: '8px 10px' }}>
                <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>TRADEABLE CASH</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#36c98e' }}>
                  {settlementReport ? `TZS ${fmtTZS(settlementReport.tradeable_cash)}` : '—'}
                </div>
              </div>
              <div style={{ background: '#0a0d10', borderRadius: 6, border: '1px solid #2a3540', padding: '8px 10px' }}>
                <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 3 }}>NET SETTLEMENT</div>
                {settlementReport ? (() => {
                  const net = settlementReport.receivables - settlementReport.committed_cash;
                  return (
                    <div style={{ fontSize: 14, fontWeight: 700, color: net >= 0 ? '#36c98e' : '#ff5c5c' }}>
                      {net >= 0 ? '+' : ''}TZS {fmtTZS(net)}
                    </div>
                  );
                })() : <div style={{ fontSize: 14, fontWeight: 700, color: '#7e8a96' }}>—</div>}
              </div>
            </div>
          </Panel>

          {inventoryBySymbol.size > 0 && (
            <Panel title="Inventory skew" badge={filterLabel}>
              {Array.from(inventoryBySymbol.entries())
                .filter(([secId]) => !filterSym || symMatches(secId, filterSym))
                .map(([secId, inv]) => {
                  const label = INSTRUMENTS.find(i => i.secId.toUpperCase() === secId.toUpperCase())?.label ?? secId;
                  const skewPct = Math.abs(inv.skew) * 100;
                  const skewColor = inv.skew > 0.1 ? '#36c98e' : inv.skew < -0.1 ? '#ff5c5c' : '#7e8a96';
                  return (
                    <div key={secId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #0a0d10' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#d7dde4', width: 52, flexShrink: 0 }}>{label}</span>
                      <div style={{ position: 'relative', flex: 1, height: 7, backgroundColor: '#0a0d10', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          position: 'absolute', top: 0, bottom: 0,
                          width: `${skewPct / 2}%`,
                          left: inv.skew >= 0 ? '50%' : undefined,
                          right: inv.skew < 0 ? '50%' : undefined,
                          backgroundColor: skewColor, borderRadius: 3,
                        }} />
                        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, backgroundColor: '#2a3540' }} />
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0, fontSize: 9, fontWeight: 700 }}>
                        <span style={{ color: inv.can_buy ? '#36c98e' : '#2a3540' }}>{inv.can_buy ? '●B' : '✕B'}</span>
                        <span style={{ color: inv.can_sell ? '#36c98e' : '#2a3540' }}>{inv.can_sell ? '●S' : '✕S'}</span>
                      </div>
                    </div>
                  );
                })}
            </Panel>
          )}

          <Panel title="Session activity">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { label: 'Total Quotes', value: performanceSnapshot ? performanceSnapshot.quotes_generated.toLocaleString() : '—' },
                { label: 'Total Fills', value: performanceSnapshot ? performanceSnapshot.fills_processed.toLocaleString() : '—' },
                { label: 'Volume (TZS)', value: totalVolume > 0 ? `TZS ${fmtTZS(totalVolume)}` : '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#7a8693' }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                </div>
              ))}
            </div>
          </Panel>

        </div>
      </div>
    </div>
  );
}
